import React, { useState, useEffect, useMemo } from 'react';
import { formatNumber } from './utils';
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, LabelList, Cell } from 'recharts';
import ZeroQtyFlashingBar from './ZeroQtyFlashingBar';

const API_URL = process.env.REACT_APP_API_URL || '';

function FlashingRedText({ children }) {
  const [flash, setFlash] = React.useState(false);
  React.useEffect(() => {
    const interval = setInterval(() => setFlash(f => !f), 500);
    return () => clearInterval(interval);
  }, []);
  return (
    <div
      style={{
        marginTop: 2,
        fontWeight: 900,
        fontSize: 13,
        letterSpacing: 1,
        textAlign: 'center',
        padding: '2px 8px',
        border: '1.5px solid #b71c1c',
        borderRadius: 7,
        background: flash ? '#b71c1c' : '#fff',
        color: flash ? '#fff' : '#b71c1c',
        boxShadow: flash ? '0 0 6px 1px #b71c1c33' : undefined,
        transition: 'background 0.2s, color 0.2s',
        animation: 'none',
        display: 'inline-block',
      }}
    >
      {children}
    </div>
  );
}

// Custom shape for zero qty bar
const FlashingBarShape = (props) => {
  const { x, y, width, height } = props;
  return (
    <foreignObject x={x} y={y} width={width} height={height} style={{overflow:'visible'}}>
      <div style={{width: width, height: height, display:'flex', alignItems:'flex-end', justifyContent:'center'}}>
        <ZeroQtyFlashingBar />
      </div>
    </foreignObject>
  );
};

// Color logic for currentQty (same as dashboard)
function getQtyColor(currentQty, minQty) {
  const cur = Number(currentQty);
  const min = Number(minQty);
  if (min === 0) return '#43a047'; // Always green if minQty is 0
  if (cur < min) return '#e53935'; // red
  if (cur === min) return '#fb8c00'; // orange
  if (cur < min + 2000) return '#fbc02d'; // yellow
  if (cur > min + 5000) return '#43a047'; // green
  // gradient between yellow and green
  const percent = Math.min((cur - min - 2000) / 3000, 1);
  return `linear-gradient(90deg, #fbc02d ${100 - percent * 100}%, #43a047 ${percent * 100}%)`;
}

function BelowMinBarChart({ cacheKey, onCacheInvalidate, overRunsActive }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // Smart caching state
  const [cachedData, setCachedData] = useState(null);
  const [lastCacheTime, setLastCacheTime] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);

  // Cache expiry time (5 minutes)
  const CACHE_EXPIRY_MS = 5 * 60 * 1000;

  // Fetch below-minimum items
  useEffect(() => {
    setLoading(true);
    setError('');
    
    // includeZeroMin based on overRunsActive flag
    const includeZeroMin = overRunsActive ? 'true' : 'false';
    
    fetch(`${API_URL}/items/below-minimum?includeZeroMin=${includeZeroMin}`, {
      headers: {
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache'
      }
    })
      .then(res => {
        if (!res.ok) throw new Error('Failed to fetch below-minimum items');
        return res.json();
      })
      .then(data => {
        if (!Array.isArray(data)) {
          throw new Error('Invalid data format received');
        }
        setItems(data);
        setLoading(false);
      })
      .catch(err => {
        console.error('Hero Chart fetch error:', err);
        setError(err.message);
        setLoading(false);
      });
  }, [cacheKey, overRunsActive]);

  // Check if cache is valid
  const isCacheValid = useMemo(() => {
    if (!cachedData || !lastCacheTime) return false;
    const now = Date.now();
    return (now - lastCacheTime) < CACHE_EXPIRY_MS;
  }, [cachedData, lastCacheTime]);

  // Process data for chart (expensive operation)
  const processChartData = useMemo(() => {
    if (!Array.isArray(items) || items.length === 0) {
      return [];
    }
    
    // Items are already filtered by backend - no need to filter again
    // Prepare data for the chart
    let data = items.map(item => {
      const minQty = Number(item.minQty);
      const currentQty = Number(item.currentQty);
      
      // Calculate percent of minQty
      let percent = 100;
      if (minQty > 0) {
        percent = Math.round((currentQty / minQty) * 100);
        if (percent > 100) percent = 100;
        if (percent < 0) percent = 0;
      }
      return {
        name: item.mainCategory,
        productCode: item.productCode,
        customer: item.client?.name || '',
        currentQty,
        minQty,
        percent,
        color: getQtyColor(currentQty, minQty),
        variantDesc: item.variantDesc,
        variant: item.variant,
        uniqueKey: `${item.productCode}_${item.mainCategory || ''}_${item.variantDesc || item.variant || ''}`
      };
    });

    // Sort: items with minQty=0 at the bottom, sorted by currentQty descending (most in stock is lowest)
    // If more than one, the one with currentQty closest to zero is highest
    data = [
      ...data.filter(d => d.minQty !== 0),
      ...data.filter(d => d.minQty === 0).sort((a, b) => a.currentQty - b.currentQty)
    ];

    setIsProcessing(false);
    return data;
  }, [items]);

  // Clear cache when items change to ensure fresh data is displayed
  useEffect(() => {
    setCachedData(null);
    setLastCacheTime(0);
  }, [items.length]);

  // Update cache when data changes or cache expires
  useEffect(() => {
    if (!isCacheValid || cacheKey !== cachedData?.cacheKey) {
      const newCacheData = {
        data: processChartData,
        cacheKey: cacheKey,
        timestamp: Date.now()
      };
      setCachedData(newCacheData);
      setLastCacheTime(Date.now());
    }
  }, [processChartData, isCacheValid, cacheKey, cachedData]);

  // Background refresh every 5 minutes
  useEffect(() => {
    const interval = setInterval(() => {
      if (onCacheInvalidate) {
        onCacheInvalidate();
      }
    }, CACHE_EXPIRY_MS);

    return () => clearInterval(interval);
  }, [onCacheInvalidate]);

  // Use cached data if available, otherwise use processed data
  const chartData = isCacheValid && cachedData ? cachedData.data : processChartData;

  // Show loading state FIRST (before checking items array)
  if (loading) {
    return (
      <div style={{ width: '100%', maxWidth: 950, margin: '0 auto', marginBottom: 8, background: '#fff', borderRadius: 32, boxShadow: '0 8px 32px #e5393512', padding: '32px', textAlign: 'center' }}>
        <h3 style={{marginTop: 2, marginBottom: 16, color:'#b71c1c', fontWeight:800, fontSize: '1.5rem', letterSpacing:1}}>Stock Below Minimum (All Customers)</h3>
        <div style={{color:'#1976d2',fontSize:16}}>Loading chart data...</div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div style={{ width: '100%', maxWidth: 950, margin: '0 auto', marginBottom: 8, background: '#fff', borderRadius: 32, boxShadow: '0 8px 32px #e5393512', padding: '32px', textAlign: 'center' }}>
        <h3 style={{marginTop: 2, marginBottom: 16, color:'#b71c1c', fontWeight:800, fontSize: '1.5rem', letterSpacing:1}}>Stock Below Minimum (All Customers)</h3>
        <div style={{color:'#d32f2f',fontSize:16}}>Error loading chart: {error}</div>
      </div>
    );
  }

  if (chartData.length === 0) {
    return (
      <div style={{ width: '100%', maxWidth: 950, margin: '0 auto', marginBottom: 8, background: '#fff', borderRadius: 32, boxShadow: '0 8px 32px #e5393512', padding: '32px', textAlign: 'center' }}>
        <h3 style={{marginTop: 2, marginBottom: 16, color:'#b71c1c', fontWeight:800, fontSize: '1.5rem', letterSpacing:1}}>Stock Below Minimum (All Customers)</h3>
        <div style={{color:'#888',fontSize:16}}>No items below minimum quantity.</div>
      </div>
    );
  }

  // Use cached chart data
  const data = chartData;

  return (
    <div style={{ width: '100%', maxWidth: 950, margin: '0 auto', marginBottom: 8, background: '#fff', borderRadius: 32, boxShadow: '0 8px 32px #e5393512', padding: '10px 32px 0px 32px', display:'flex', flexDirection:'column', alignItems:'center' }}>
      <h3 style={{marginTop: 2, marginBottom: 6, color:'#b71c1c', fontWeight:800, fontSize: '1.5rem', letterSpacing:1}}>Stock Below Minimum (All Customers)</h3>
      <ResponsiveContainer width="100%" height={420}>
        <BarChart data={data} margin={{ top: 16, right: 48, left: 0, bottom: 60 }} key={JSON.stringify(data.map(d => d.productCode || d.name))}>
          <XAxis
            dataKey="uniqueKey"
            interval={0}
            height={60}
            tick={({ x, y, payload, index }) => {
              const entry = data[index];
              const variant = entry?.variantDesc || entry?.variant || '';
              const showZero = entry?.minQty > 0 && entry?.currentQty === 0;
              return (
                <g transform={`translate(${x},${y + 10})`}>
                  <text x={0} y={0} dy={0} textAnchor="middle" style={{fontSize:13,fill:'#1976d2',fontWeight:700}}>
                    {variant}
                  </text>
                  {showZero && (
                    <foreignObject x={-40} y={2} width={80} height={24} style={{overflow:'visible'}}>
                      <div style={{display:'flex',justifyContent:'center',alignItems:'center',width:'100%'}}>
                        <FlashingRedText>Stock level at 0</FlashingRedText>
                      </div>
                    </foreignObject>
                  )}
                </g>
              );
            }} />
          <YAxis type="number" domain={[0, 100]} tickFormatter={v => v + '%'} label={{ value: '% of Min Qty', angle: -90, position: 'insideLeft', offset: 0, style: { fill: '#888', fontSize: 12, fontWeight: 600 } }} tick={{fontSize:13,fill:'#1976d2'}} />
          <Tooltip content={({ active, payload }) => {
            if (active && payload && payload.length) {
              const d = payload[0].payload;
              return (
                <div style={{ background: '#fff', border: '1px solid #ccc', borderRadius: 8, padding: 12, boxShadow: '0 2px 8px #8882' }}>
                  <div style={{ fontWeight: 700, color: '#1565c0', marginBottom: 6, fontSize: 16 }}>{d.customer || 'Unknown Customer'}</div>
                  <div style={{ fontWeight: 600, color: '#b71c1c', marginBottom: 4 }}>{d.name}</div>
                  <div><b>Product Code:</b> {d.productCode}</div>
                  <div><b>Current Qty:</b> {formatNumber(d.currentQty)}</div>
                  <div><b>Min Qty:</b> {formatNumber(d.minQty)}</div>
                </div>
              );
            }
            return null;
          }} />
          <Legend verticalAlign="top" height={36} content={() => (
            <div style={{display:'flex',gap:24,alignItems:'center',marginLeft:16,marginBottom:8}}>
              <span style={{display:'flex',alignItems:'center',gap:6}}>
                <span style={{display:'inline-block',width:18,height:18,background:'#bdbdbd',borderRadius:4,marginRight:3,verticalAlign:'middle'}}></span>
                <span style={{color:'#bdbdbd', fontSize:15}}>Min Qty</span>
              </span>
              <span style={{display:'flex',alignItems:'center',gap:6}}>
                <span style={{display:'inline-block',width:18,height:18,background:'#e53935',borderRadius:4,marginRight:3,verticalAlign:'middle'}}></span>
                <span style={{color:'#e53935', fontSize:15}}>Current Qty</span>
              </span>
            </div>
          )}/>
          <Bar dataKey="percent" name="Stock Level" barSize={28} >
            {data.map((entry, idx) => (
              <Cell key={idx} fill={entry.color} />
            ))}
            <LabelList dataKey="percent" position="top" style={{fontSize:11, fontWeight:700, fill:'#1565c0'}} formatter={v => `${v}%`}/>
          </Bar>
        </BarChart>
      </ResponsiveContainer>


    </div>
  );
}

export default BelowMinBarChart;
